import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APP_CONFIG } from '../../../configs/config';
import { useAuth } from '../../../contexts/AuthContext';

const FETCH_TIMEOUT_MS = 12000;

const defaultState = {
  contacts: [],
  hasError: false,
  errorMessage: '',
};

export default function useContactData() {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [contacts, setContacts] = useState(defaultState.contacts);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(defaultState.hasError);
  const [errorMessage, setErrorMessage] = useState(
    defaultState.errorMessage,
  );
  const abortControllerRef = useRef(null);

  const username = useMemo(() => user?.username?.trim() || null, [user]);

  const resetState = useCallback(() => {
    setContacts(defaultState.contacts);
    setHasError(defaultState.hasError);
    setErrorMessage(defaultState.errorMessage);
  }, []);

  const buildFriendlyError = useCallback((error) => {
    const message = error?.message || 'Unable to load contacts.';
    if (message.includes('Failed to fetch')) {
      return 'Network error. Check your connection and try again.';
    }
    if (message.includes('timeout')) {
      return 'Request timed out. Please retry in a moment.';
    }
    if (message.length > 160) {
      return 'Something went wrong while loading contacts.';
    }
    return message;
  }, []);

  const fetchContacts = useCallback(async () => {
    if (!isAuthenticated || !username) {
      resetState();
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(
        `${APP_CONFIG.API_BASE_URL}/business-cards/${encodeURIComponent(
          username,
        )}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        let detail = `Failed with status ${response.status}`;
        try {
          const data = await response.json();
          detail = data?.detail || detail;
        } catch (jsonError) {
          /* ignore parsing error */
        }
        throw new Error(detail);
      }

      const data = await response.json();
      setContacts(Array.isArray(data?.contacts) ? data.contacts : []);
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      setHasError(true);
      setErrorMessage(buildFriendlyError(error));
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  }, [isAuthenticated, username, resetState, buildFriendlyError]);

  useEffect(() => {
    if (!isAuthenticated || !username) {
      resetState();
      return undefined;
    }
    fetchContacts();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isAuthenticated, username, fetchContacts, resetState]);

  return {
    isAuthenticated,
    isLoading: isAuthLoading || isLoading,
    hasError,
    errorMessage,
    contacts,
    refetch: fetchContacts,
  };
}
